<template>
  <aside
    class="adm-sidebar adm-scroll"
    :class="{ 'is-collapsed': collapsed, 'is-open': drawerOpen }"
    aria-label="Navegación administrativa"
  >
    <div class="adm-sidebar__brand">
      <router-link to="/admin" class="adm-sidebar__logo" aria-label="Panel administrativo Selk">
        <img :src="logo" alt="Selk" />
      </router-link>
      <button
        type="button"
        class="adm-sidebar__collapse"
        :aria-label="collapsed ? 'Expandir menú' : 'Contraer menú'"
        @click="$emit('toggle-collapse')"
      >
        <component :is="collapsed ? ChevronsRight : ChevronsLeft" :size="18" />
      </button>
    </div>

    <nav class="adm-sidebar__nav">
      <div v-for="group in nav" :key="group.section" class="adm-sidebar__group">
        <p v-show="!collapsed" class="adm-section-label adm-sidebar__group-title">
          {{ group.section }}
        </p>
        <ul class="adm-sidebar__list">
          <li v-for="item in group.items" :key="item.name">
            <router-link
              :to="item.to"
              class="adm-sidebar__item"
              :class="{ 'is-active': isActive(item) }"
              :aria-current="isActive(item) ? 'page' : undefined"
              :title="collapsed ? item.label : undefined"
              @click="$emit('navigate')"
            >
              <component :is="item.icon" :size="19" class="adm-sidebar__icon" />
              <span v-show="!collapsed" class="adm-sidebar__label">{{ item.label }}</span>
            </router-link>
          </li>
        </ul>
      </div>
    </nav>

    <router-link to="/" class="adm-sidebar__exit" :title="collapsed ? 'Ir al sitio público' : undefined">
      <Globe :size="18" class="adm-sidebar__icon" />
      <span v-show="!collapsed">Ver sitio público</span>
    </router-link>
  </aside>
</template>

<script>
/**
 * Barra lateral del panel — plan 27_09_2026 §4.2.
 *
 * Sustituye a los tres bloques `#menu`/`.menu-group` duplicados (AdminPage,
 * Gastos, Ingresos) y a las cuatro barras de pestañas de Print3DAdmin/*.
 * No conoce ninguna ruta: todo sale de `config/adminNav.js`.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { ChevronsLeft, ChevronsRight, Globe } from 'lucide-vue-next';
import { adminNav, findNavItem } from '@/config/adminNav.js';
import logo from '@/assets/SELK_LOGO.svg';

export default {
  name: 'AdminSidebar',
  // `Globe` se usa como etiqueta estática (`<Globe />`), y en Options API los
  // valores devueltos por `setup()` NO participan en la resolución de
  // componentes: hay que registrarlos aquí. `ChevronsLeft`/`ChevronsRight` sí
  // van por `setup`, porque se consumen como valores en `<component :is>`.
  components: { Globe },
  props: {
    collapsed: { type: Boolean, default: false },
    drawerOpen: { type: Boolean, default: false },
  },
  emits: ['toggle-collapse', 'navigate'],
  setup() {
    const route = useRoute();
    // Un solo ítem activo a la vez, incluso en las vistas de AdminPage que
    // comparten ruta y se distinguen por query string (criterio C12).
    const activeItem = computed(() => findNavItem(route));
    const isActive = (item) => activeItem.value?.name === item.name;

    return { nav: adminNav, isActive, logo, ChevronsLeft, ChevronsRight };
  },
};
</script>

<style scoped>
.adm-sidebar {
  display: flex;
  flex-direction: column;
  gap: var(--adm-gap);
  height: 100vh;
  position: sticky;
  top: 0;
  padding: var(--adm-gap) 0.75rem;
  background-color: var(--adm-sidebar-bg);
  border-right: 1px solid var(--adm-border);
  box-sizing: border-box;
  overflow-y: auto;
}

.adm-sidebar__brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--adm-gap-sm);
  min-height: calc(var(--adm-topbar-height) - var(--adm-gap));
}

.adm-sidebar__logo img {
  display: block;
  width: 100%;
  max-width: 120px;
  height: auto;
}

.adm-sidebar__collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 0;
  background: none;
  border: 1px solid var(--adm-border);
  border-radius: var(--adm-radius-sm);
  color: var(--color-text);
  cursor: pointer;
}

.adm-sidebar__collapse:hover {
  border-color: var(--color-brand);
  color: var(--color-brand);
}

.adm-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: var(--adm-gap-lg);
  flex: 1;
}

.adm-sidebar__group-title {
  margin: 0 0 0.4rem 0.6rem;
}

.adm-sidebar__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.adm-sidebar__item {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.55rem 0.6rem;
  border-radius: var(--adm-radius-sm);
  color: var(--color-text);
  font-family: var(--font-body);
  font-size: var(--adm-font-base);
  text-decoration: none;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.adm-sidebar__item:hover {
  background-color: var(--adm-surface-alt);
  color: var(--color-title);
}

/* Ítem activo: el teal de marca es el acento más constante de la identidad
   (manualMarca §5.2), así que es el que marca "dónde estoy". */
.adm-sidebar__item.is-active {
  background-color: var(--adm-info-bg);
  color: var(--color-brand);
  font-weight: 600;
}

.adm-sidebar__icon { flex-shrink: 0; }

.adm-sidebar__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adm-sidebar__exit {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.55rem 0.6rem;
  border-top: 1px solid var(--adm-border);
  padding-top: var(--adm-gap);
  color: var(--color-text);
  font-size: var(--adm-font-sm);
  text-decoration: none;
}

.adm-sidebar__exit:hover { color: var(--color-brand); }

/* --- Colapsado: sólo iconos, centrados --- */
.adm-sidebar.is-collapsed {
  padding-inline: 0.5rem;
  align-items: stretch;
}

.adm-sidebar.is-collapsed .adm-sidebar__brand { justify-content: center; }
.adm-sidebar.is-collapsed .adm-sidebar__logo { display: none; }

.adm-sidebar.is-collapsed .adm-sidebar__item,
.adm-sidebar.is-collapsed .adm-sidebar__exit {
  justify-content: center;
  padding-inline: 0;
}

/* --- Tablet: forzado a iconos, sin botón de colapso --- */
@media (max-width: 1023px) {
  .adm-sidebar { padding-inline: 0.5rem; }
  .adm-sidebar__logo,
  .adm-sidebar__collapse { display: none; }
  .adm-sidebar__group-title { display: none; }
  .adm-sidebar__item,
  .adm-sidebar__exit { justify-content: center; padding-inline: 0; }
  .adm-sidebar__label,
  .adm-sidebar__exit span { display: none; }
}

/* --- Móvil: cajón deslizante --- */
@media (max-width: 767px) {
  .adm-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--adm-z-sidebar);
    width: var(--adm-sidebar-width);
    padding-inline: 0.75rem;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
    box-shadow: var(--adm-shadow-lg);
  }

  .adm-sidebar.is-open { transform: translateX(0); }

  /* Dentro del cajón el menú vuelve a mostrarse completo, con etiquetas. */
  .adm-sidebar.is-open .adm-sidebar__logo { display: block; }
  .adm-sidebar.is-open .adm-sidebar__group-title { display: block; }
  .adm-sidebar.is-open .adm-sidebar__item,
  .adm-sidebar.is-open .adm-sidebar__exit {
    justify-content: flex-start;
    padding-inline: 0.6rem;
  }
  .adm-sidebar.is-open .adm-sidebar__label,
  .adm-sidebar.is-open .adm-sidebar__exit span { display: inline; }
}
</style>
